import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppStore } from '../stores/appStore';
import { unlockArchivedPuzzle, fetchPuzzleList } from '../services/api';
import { ThresholdLockedModal } from './ThresholdLockedModal';
import './PuzzleListModal.css';

interface PuzzleListModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const PUZZLES_PER_PAGE = 20;
const ARCHIVE_UNLOCK_COST = 100;

export default function PuzzleListModal({ isOpen, onClose }: PuzzleListModalProps) {
    const [page, setPage] = useState(0);
    const [unlockPuzzleId, setUnlockPuzzleId] = useState<string | null>(null);
    const [unlockLoading, setUnlockLoading] = useState(false);
    const [unlockError, setUnlockError] = useState<string | null>(null);

    // Threshold lock modal state
    const [thresholdModalData, setThresholdModalData] = useState<{
        puzzleId: string;
        puzzleLabel: string;
        mode: string;
        requiredThreshold: number;
        currentCompletion: number;
    } | null>(null);

    const puzzles = useAppStore(state => state.puzzles);
    const setPuzzles = useAppStore(state => state.setPuzzles);
    const currentPuzzleId = useAppStore(state => state.currentPuzzleId);
    const currentMode = useAppStore(state => state.currentMode);
    const setCurrentPuzzleId = useAppStore(state => state.setCurrentPuzzleId);
    const setGameState = useAppStore(state => state.setGameState);
    const user = useAppStore(state => state.user);
    const setUser = useAppStore(state => state.setUser);

    // Reset page when modal opens
    useEffect(() => {
        if (isOpen) {
            setPage(0);
            setUnlockPuzzleId(null);
            setUnlockError(null);
            setThresholdModalData(null);
        }
    }, [isOpen]);

    if (!puzzles) return null;

    // Filter puzzles:
    // 1. Must match current mode
    // 2. Must not be in the future (proper date parsing for DD/MM/YYYY format)
    const availablePuzzles = puzzles.filter(p => {
        if (p.mode !== currentMode) return false;

        // Filter future puzzles if display_date is available
        if (p.display_date) {
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Normalize to start of day

            // Parse DD/MM/YYYY format
            const parts = p.display_date.split('/');
            if (parts.length === 3) {
                const puzzleDate = new Date(
                    parseInt(parts[2], 10),  // Year
                    parseInt(parts[1], 10) - 1,  // Month (0-indexed)
                    parseInt(parts[0], 10)   // Day
                );
                if (puzzleDate > today) return false;
            }
        }

        return true;
    });

    const totalPuzzles = availablePuzzles.length;
    const totalPages = Math.ceil(totalPuzzles / PUZZLES_PER_PAGE);

    const start = page * PUZZLES_PER_PAGE;
    const end = Math.min(start + PUZZLES_PER_PAGE, totalPuzzles);
    const currentPuzzles = availablePuzzles.slice(start, end);

    const handlePrev = () => {
        if (page > 0) setPage(p => p - 1);
    };

    const handleNext = () => {
        if (page < totalPages - 1) setPage(p => p + 1);
    };

    const handlePuzzleClick = (puzzleId: string, isLocked: boolean, isThresholdLocked: boolean, puzzle: typeof puzzles[0]) => {
        // Handle threshold lock - show threshold modal
        if (isThresholdLocked) {
            setThresholdModalData({
                puzzleId: puzzle.id,
                puzzleLabel: puzzle.label || puzzle.id,
                mode: puzzle.mode,
                requiredThreshold: puzzle.min_completion_threshold || 0,
                currentCompletion: puzzle.user_completion_pct || 0,
            });
            return;
        }

        // Handle archive lock - show unlock confirmation
        if (isLocked) {
            setUnlockPuzzleId(puzzleId);
            setUnlockError(null);
            return;
        }

        if (puzzleId !== currentPuzzleId) {
            setGameState(null); // Clear current game state
            setCurrentPuzzleId(puzzleId); // This should trigger a refetch in the app logic
            onClose();
        } else {
            onClose();
        }
    };

    const handleUnlockConfirm = async () => {
        if (!unlockPuzzleId) return;

        setUnlockLoading(true);
        setUnlockError(null);

        try {
            const result = await unlockArchivedPuzzle(unlockPuzzleId);
            if (result.success) {
                // Update user coins
                if (user && result.coins_remaining !== undefined) {
                    setUser({ ...user, coins: result.coins_remaining });
                }

                // Refresh puzzle list to get updated is_unlocked status
                const { puzzles: updatedPuzzles } = await fetchPuzzleList(currentMode);
                setPuzzles(updatedPuzzles);

                // Navigate to the unlocked puzzle
                setGameState(null);
                setCurrentPuzzleId(unlockPuzzleId);
                onClose();
            } else {
                setUnlockError(result.message);
            }
        } catch (err) {
            setUnlockError(err instanceof Error ? err.message : 'Failed to unlock puzzle');
        } finally {
            setUnlockLoading(false);
        }
    };

    const handleUnlockCancel = () => {
        setUnlockPuzzleId(null);
        setUnlockError(null);
    };

    const userCoins = user?.coins ?? 0;
    const canAfford = userCoins >= ARCHIVE_UNLOCK_COST;

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="profile-modal-overlay" onClick={onClose}>
                    <motion.div
                        className="profile-modal" // Reusing profile modal wrapper style
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        onClick={e => e.stopPropagation()}
                        style={{ maxWidth: '600px', width: '95%' }}
                    >
                        <div className="puzzle-list-header">
                            <h2 className="puzzle-list-title">
                                Puzzles {start + 1} - {end}
                            </h2>
                            <button className="profile-modal-close" onClick={onClose}>×</button>
                        </div>

                        <div className="puzzle-list-modal-content">
                            {/* Unlock Confirmation Panel */}
                            {unlockPuzzleId && (
                                <div className="unlock-confirmation">
                                    <div className="unlock-icon">🔓</div>
                                    <h3>Unlock Archived Puzzle?</h3>
                                    <p className="unlock-cost">
                                        Cost: <strong>{ARCHIVE_UNLOCK_COST}</strong> coins
                                    </p>
                                    <p className="unlock-balance">
                                        Your balance: <strong>{userCoins}</strong> coins
                                    </p>
                                    {unlockError && (
                                        <p className="unlock-error">{unlockError}</p>
                                    )}
                                    {!canAfford && !unlockError && (
                                        <p className="unlock-error">Not enough coins!</p>
                                    )}
                                    <div className="unlock-actions">
                                        <button
                                            className="unlock-btn cancel"
                                            onClick={handleUnlockCancel}
                                            disabled={unlockLoading}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className="unlock-btn confirm"
                                            onClick={handleUnlockConfirm}
                                            disabled={!canAfford || unlockLoading}
                                        >
                                            {unlockLoading ? 'Unlocking...' : 'Unlock'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {!unlockPuzzleId && (
                                <>
                                    <div className="puzzle-list-controls">
                                        <span className="puzzle-list-page-info">
                                            Page {page + 1} of {totalPages || 1}
                                        </span>
                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                            <button
                                                className="puzzle-control-btn"
                                                onClick={handlePrev}
                                                disabled={page === 0}
                                                aria-label="Previous page"
                                            >
                                                ‹
                                            </button>
                                            <button
                                                className="puzzle-control-btn"
                                                onClick={handleNext}
                                                disabled={page >= totalPages - 1}
                                                aria-label="Next page"
                                            >
                                                ›
                                            </button>
                                        </div>
                                    </div>

                                    <div className="puzzle-list-body">
                                        <div className="puzzle-grid">
                                            {currentPuzzles.map((puzzle, index) => {
                                                const globalIndex = start + index + 1;
                                                const isActive = puzzle.id === currentPuzzleId;
                                                const isArchiveLocked = puzzle.is_archived && !puzzle.is_unlocked;
                                                const isThresholdLocked = puzzle.is_locked_by_threshold || false;
                                                const isLocked = isArchiveLocked || isThresholdLocked;

                                                return (
                                                    <div
                                                        key={puzzle.id}
                                                        className={`puzzle-box ${puzzle.completed ? 'completed' : 'incomplete'} ${isActive ? 'active' : ''} ${isLocked ? 'locked' : ''} ${isThresholdLocked ? 'threshold-locked' : ''}`}
                                                        onClick={() => handlePuzzleClick(puzzle.id, !!isArchiveLocked, isThresholdLocked, puzzle)}
                                                    >
                                                        <span className="puzzle-box-number">{globalIndex}</span>
                                                        <div className="puzzle-box-status">
                                                            {isThresholdLocked ? (
                                                                <span className="puzzle-lock-icon threshold">🔐</span>
                                                            ) : isArchiveLocked ? (
                                                                <span className="puzzle-lock-icon">🔒</span>
                                                            ) : puzzle.completed ? (
                                                                <img
                                                                    src="/static/check-2.png"
                                                                    alt="Completed"
                                                                    className="puzzle-status-icon"
                                                                />
                                                            ) : (
                                                                <img
                                                                    src="/static/cancel.png"
                                                                    alt="Incomplete"
                                                                    className="puzzle-status-icon"
                                                                />
                                                            )}
                                                        </div>
                                                        {isThresholdLocked && puzzle.min_completion_threshold && (
                                                            <span className="threshold-badge">{puzzle.min_completion_threshold}%</span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div className="puzzle-list-controls bottom">
                                        <div style={{ flex: 1 }}></div> {/* Spacer */}
                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                            <button
                                                className="puzzle-control-btn"
                                                onClick={handlePrev}
                                                disabled={page === 0}
                                                aria-label="Previous page"
                                            >
                                                ‹
                                            </button>
                                            <button
                                                className="puzzle-control-btn"
                                                onClick={handleNext}
                                                disabled={page >= totalPages - 1}
                                                aria-label="Next page"
                                            >
                                                ›
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </motion.div>
                </div>
            )}

            {/* Threshold Locked Modal */}
            <ThresholdLockedModal
                isOpen={thresholdModalData !== null}
                onClose={() => setThresholdModalData(null)}
                puzzleLabel={thresholdModalData?.puzzleLabel || ''}
                mode={thresholdModalData?.mode || 'classic'}
                requiredThreshold={thresholdModalData?.requiredThreshold || 0}
                currentCompletion={thresholdModalData?.currentCompletion || 0}
            />
        </AnimatePresence>
    );
}
